import type {
  ConductorBundle,
  TechCalcDictionaries,
  TechCalcDrawing,
  TechCalcLineFeature,
  TechCalcPointFeature,
  TechCalcPointProperties,
} from "./types";
import { isLineFeature, isPointFeature } from "./types";

// ── refs (S1, S2… / A1, A2…) ─────────────────────────────────────────────────
export const REF_PREFIX = { pole: "S", access: "A" } as const;

/** Next free ref for a node kind, scanning the refs currently in use. */
export function nextRef(usedRefs: Iterable<string>, kind: "pole" | "access"): string {
  const prefix = REF_PREFIX[kind];
  let max = 0;
  for (const r of usedRefs) {
    const m = new RegExp(`^${prefix}(\\d+)$`).exec(r);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `${prefix}${max + 1}`;
}

/** Display label priority (owner request): numer historyczny → numer pełny → ref. */
export function poleDisplayLabel(p: TechCalcPointProperties): string {
  return p.params.historical_number?.trim() || p.params.full_number?.trim() || p.ref;
}

/** Natural ref ordering: S1, S2, S10 (not S1, S10, S2); poles before access lines. */
export function compareRefs(a: string, b: string): number {
  const pa = a[0] ?? "";
  const pb = b[0] ?? "";
  if (pa !== pb) return pa === "S" ? -1 : pb === "S" ? 1 : pa.localeCompare(pb);
  const na = parseInt(a.slice(1), 10);
  const nb = parseInt(b.slice(1), 10);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a.localeCompare(b, "pl");
}

// ── formatting ───────────────────────────────────────────────────────────────
export function fmtMeters(m: number | null | undefined, digits = 1): string {
  if (m == null || !Number.isFinite(m)) return "—";
  return `${m.toLocaleString("pl-PL", { maximumFractionDigits: digits, minimumFractionDigits: 0 })} m`;
}

/** Force values come unit-tagged from the backend descriptions; numbers shown raw. */
export function fmtForce(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("pl-PL", { maximumFractionDigits: 2 });
}

/** Linie przebiegu obliczeń dla osi: przebieg z podstawionymi wartościami, a gdy
 *  go nie ma (wyniki policzone starszą wersją silnika) — sam wzór symboliczny. */
export function calcSteps(
  steps: string[] | undefined,
  description: string[] | undefined,
): string[] {
  return steps?.length ? steps : (description ?? []);
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("pl-PL");
}

/** "AsXSn 70×4" — compact bundle description. */
export function fmtBundle(b: ConductorBundle): string {
  return `${b.family} ${b.cross_section}×${b.count}`;
}

export function fmtBundles(bundles: ConductorBundle[]): string {
  return bundles.length ? bundles.map(fmtBundle).join(", ") : "— brak przewodów —";
}

/** Roman numeral for span numbering on the calculation card (P.I, P.II, …). */
export function roman(n: number): string {
  const table: [number, string][] = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"],
    [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let out = "";
  let rest = n;
  for (const [v, s] of table) {
    while (rest >= v) {
      out += s;
      rest -= v;
    }
  }
  return out || "0";
}

// ── geometry (no Leaflet dependency — usable on the print card) ──────────────
const EARTH_R = 6371008.8;

export function haversineM(a: [number, number], b: [number, number]): number {
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(s));
}

export function lineLengthM(coords: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) total += haversineM(coords[i - 1], coords[i]);
  return total;
}

// ── drawing helpers ──────────────────────────────────────────────────────────
export function drawingPoints(fc: TechCalcDrawing | null | undefined): TechCalcPointFeature[] {
  return (fc?.features ?? []).filter(isPointFeature);
}

export function drawingLines(fc: TechCalcDrawing | null | undefined): TechCalcLineFeature[] {
  return (fc?.features ?? []).filter(isLineFeature);
}

/** Spans touching the given node ref. */
export function spansOf(fc: TechCalcDrawing | null | undefined, ref: string): TechCalcLineFeature[] {
  return drawingLines(fc).filter(
    (l) => l.properties.from_ref === ref || l.properties.to_ref === ref,
  );
}

export const EMPTY_DRAWING: TechCalcDrawing = { type: "FeatureCollection", features: [] };

// ── dictionaries ─────────────────────────────────────────────────────────────
export interface ConstructionGroup {
  /** null = ungrouped flat list. */
  group: string | null;
  values: string[];
}

/** Normalize `constructions` (flat list, [{key, values}] groups, or {group: values})
 *  into option groups. */
export function constructionGroups(
  dicts: TechCalcDictionaries | undefined,
): ConstructionGroup[] {
  const c = dicts?.constructions;
  if (!c) return [];
  if (Array.isArray(c)) {
    if (c.every((item): item is string => typeof item === "string")) {
      return [{ group: null, values: c }];
    }
    return (c as { key: string; values: string[] }[]).map((g) => ({
      group: g.key,
      values: g.values ?? [],
    }));
  }
  return Object.entries(c).map(([group, values]) => ({ group, values }));
}

/** Normalize pole types to full options: prefer pole_types_all (has resolvable
 *  flags for the greyed-out "brak w katalogu norm" entries); fall back to the
 *  bare pole_types list (all resolvable by definition). */
export function poleTypeOptions(
  dicts: TechCalcDictionaries | undefined,
): { value: string; resolvable: boolean }[] {
  if (dicts?.pole_types_all?.length) return dicts.pole_types_all;
  const raw = dicts?.pole_types ?? [];
  return raw.map((t) =>
    typeof t === "string" ? { value: t, resolvable: true } : t,
  );
}

// ── pole-type driven autofill (miękkie — user może nadpisać w selektach) ─────
/** Material implied by the pole type; null = no rule (e.g. Kompozytowy). */
export function materialForPoleType(poleType: string): string | null {
  const t = poleType.trim();
  if (!t) return null;
  if (t.startsWith("ŻN")) return "Żelbeton";
  if (/^E[-\s\d]/.test(t)) return "Wirowany";
  if (t.startsWith("BSW")) return "Wirowany";
  if (t.startsWith("Drewniany")) return "Drewno";
  if (t.startsWith("Kratowy")) return "Stal";
  if (t.startsWith("ALA") || t.startsWith("DANA")) return "Żelbeton";
  return null;
}

/** Height parsed out of the pole type ("ŻN-10" → "10.0", "E-10.5/2.5" → "10.5"). */
export function heightForPoleType(poleType: string): string | null {
  const m = /(\d+(?:[.,]\d+)?)/.exec(poleType);
  if (!m) return null;
  const n = Number(m[1].replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n.toFixed(1) : null;
}

// ── function suggestions from topology ───────────────────────────────────────
export interface FunctionSuggestion {
  ref: string;
  current: string;
  /** null = no suggestion (isolated pole). */
  suggested: string | null;
  /** The line branches here: 3+ conductor directions (thin przyłącza excluded). */
  hasBranch: boolean;
}

/** Angle (degrees) between two arms leaving one pole, computed at the vertex. */
function armAngleDeg(u: [number, number], v: [number, number]): number {
  const dot = u[0] * v[0] + u[1] * v[1];
  return (Math.acos(Math.min(1, Math.max(-1, dot))) * 180) / Math.PI;
}

/** Odgałęzienie = a 4-wire line of at least 35 mm²; anything thinner (2×25,
 *  2×35, 4×16 — service drops and lighting) is a przyłącze and leaves the
 *  pole function alone. Threshold agreed with the owner off the Ramsowo data:
 *  every pole marked R there carries a 4×35/4×50 line, every pure Przelotowy
 *  only 2-wire drops. */
const BRANCH_MIN_VEINS = 4;
const BRANCH_MIN_CROSS_SECTION = 35;

export function isBranchSpan(bundles: ConductorBundle[] | null | undefined): boolean {
  return (bundles ?? []).some(
    (b) => b.count >= BRANCH_MIN_VEINS && b.cross_section >= BRANCH_MIN_CROSS_SECTION,
  );
}

/** True for the three rozgałęźne entries of the FUNCTIONS dictionary. */
export function isBranchFunction(fn: string | null | undefined): boolean {
  return (fn ?? "").trim().toLowerCase().startsWith("rozgałęźno");
}

/** Default suggestion for a pole with a branch. RPP and RNK stay manual-only:
 *  whether a branch is anchored or runs through cannot be read off the drawing,
 *  and RPK is 57% of all rozgałęźne poles in the ELP4 dataset. */
const BRANCH_FUNCTION = "Rozgałęźno-przelotowo-krańcowy";

/**
 * Suggested function for every pole, from the directions the conductors leave in.
 * A direction is a pole–pole span or an access line thick enough to be an
 * odgałęzienie (isBranchSpan); thin przyłącza are ignored, they never change the
 * function. 1 direction → Krańcowy; 2 → Przelotowy (≥150°, near-collinear) or
 * Narożny; 3+ → the line branches here, so rozgałęźny (RPK).
 * Suggestion values are exact FUNCTIONS dictionary entries.
 */
export function suggestPoleFunctions(fc: TechCalcDrawing | null | undefined): FunctionSuggestion[] {
  const points = drawingPoints(fc);
  const poles = points.filter((p) => p.properties.kind === "pole");
  const nodeByRef = new Map(points.map((p) => [p.properties.ref, p]));
  const poleRefs = new Set(poles.map((p) => p.properties.ref));
  // ref → refs of the nodes a conductor direction leads to.
  const directions = new Map<string, Set<string>>();
  const add = (from: string, to: string) => {
    if (!poleRefs.has(from)) return;
    if (!directions.has(from)) directions.set(from, new Set());
    directions.get(from)!.add(to);
  };
  for (const line of drawingLines(fc)) {
    const { from_ref, to_ref, bundles } = line.properties;
    const isTrunk = poleRefs.has(from_ref) && poleRefs.has(to_ref);
    // Przyłącze (cienka linia do A#) nie tworzy kierunku — pomijamy.
    if (!isTrunk && !isBranchSpan(bundles)) continue;
    add(from_ref, to_ref);
    add(to_ref, from_ref);
  }

  const out: FunctionSuggestion[] = [];
  for (const pole of poles) {
    const ref = pole.properties.ref;
    const [lngA, latA] = pole.geometry.coordinates;
    const cosLat = Math.cos((latA * Math.PI) / 180);
    const arms: [number, number][] = [];
    for (const nRef of directions.get(ref) ?? []) {
      const other = nodeByRef.get(nRef);
      if (!other) continue;
      const [lngB, latB] = other.geometry.coordinates;
      const dx = (lngB - lngA) * cosLat;
      const dy = latB - latA;
      const len = Math.hypot(dx, dy);
      if (len > 0) arms.push([dx / len, dy / len]);
    }

    // 3+ kierunków = linia się tutaj rozgałęzia (trzecie ramię magistrali albo
    // odgałęzienie); przy 2 kierunkach nic się nie rozgałęzia, nawet gdy jeden
    // z nich prowadzi do węzła A# (linia po prostu biegnie dalej).
    const hasBranch = arms.length >= 3;
    let suggested: string | null = null;
    if (hasBranch) {
      suggested = BRANCH_FUNCTION;
    } else if (arms.length === 1) {
      suggested = "Krańcowy";
    } else if (arms.length === 2) {
      suggested = armAngleDeg(arms[0], arms[1]) >= 150 ? "Przelotowy" : "Narożny";
    }
    out.push({ ref, current: pole.properties.params.function, suggested, hasBranch });
  }
  return out.sort((a, b) => compareRefs(a.ref, b.ref));
}

// ── norm catalog (schema-agnostic tables) ────────────────────────────────────
/** Keys never shown as table columns / edit fields. */
export const NORM_HIDDEN_KEYS = new Set(["id", "created_at", "updated_at", "created_by", "updated_by"]);

/** Polish labels for the row keys we expect; anything else is humanized. */
const NORM_LABELS: Record<string, string> = {
  position: "Poz.",
  name: "Nazwa",
  label: "Nazwa",
  family: "Rodzina",
  material: "Materiał",
  material_type: "Materiał",
  construction: "Konstrukcja",
  pole_type: "Typ słupa",
  cross_section: "Przekrój [mm²]",
  // przewody — żyły per przekrój (lw_*), napór, naciągi w 4 przedziałach
  lw_16: "Żyły 16",
  lw_25: "Żyły 25",
  lw_35: "Żyły 35",
  lw_50: "Żyły 50",
  lw_70: "Żyły 70",
  lw_95: "Żyły 95",
  lw_120: "Żyły 120",
  unit_windforce: "Napór jedn.",
  stretch_bel35: "Naciąg <35",
  stretch_35_50: "Naciąg 35–50",
  stretch_50_75: "Naciąg 50–75",
  stretch_over75: "Naciąg >75",
  // słupy — nośności + wiatr
  maxload_x: "Fx dop.",
  maxload_y: "Fy dop.",
  wind_x: "Wiatr X",
  wind_y: "Wiatr Y",
  height: "Wysokość [m]",
  function: "Funkcja",
  // historia zmian
  entry_kind: "Rodzaj wpisu",
  entry_name: "Wpis",
  before: "Przed",
  after: "Po",
  version: "Wersja katalogu",
};

export function normColumnLabel(key: string): string {
  if (NORM_LABELS[key]) return NORM_LABELS[key];
  const m = /^tension_(\d)$/.exec(key);
  if (m) return `Naciąg ${roman(parseInt(m[1], 10))}`;
  const t = key.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Ordered column keys for a set of rows: preferred identity keys first, rest alphabetically. */
export function normColumns(rows: Array<Record<string, unknown>>): string[] {
  const PREFERRED = [
    "position", "name", "label", "family", "material", "material_type",
    "construction", "pole_type", "cross_section",
    "lw_16", "lw_25", "lw_35", "lw_50", "lw_70", "lw_95", "lw_120",
    "unit_windforce", "stretch_bel35", "stretch_35_50", "stretch_50_75", "stretch_over75",
    "maxload_x", "maxload_y", "wind_x", "wind_y", "height",
  ];
  const seen = new Set<string>();
  for (const row of rows) for (const k of Object.keys(row)) if (!NORM_HIDDEN_KEYS.has(k)) seen.add(k);
  const preferred = PREFERRED.filter((k) => seen.has(k));
  const rest = [...seen].filter((k) => !preferred.includes(k)).sort();
  return [...preferred, ...rest];
}

export function normCellText(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "tak" : "nie";
  if (typeof value === "number") return value.toLocaleString("pl-PL", { maximumFractionDigits: 4 });
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// ── API error extraction ({ data, errors, meta } envelope) ───────────────────
export function extractApiError(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { errors?: Record<string, unknown> | string[] | string } };
    message?: string;
  };
  if (e?.response?.status === 403) return "Brak uprawnień do tej operacji.";
  const errs = e?.response?.data?.errors;
  if (typeof errs === "string" && errs) return errs;
  if (Array.isArray(errs) && errs.length) return errs.join(" ");
  if (errs && typeof errs === "object") {
    const obj = errs as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    const flat = Object.values(obj).flat().filter(Boolean);
    if (flat.length) return flat.map(String).join(" ");
  }
  return e?.message || "Coś poszło nie tak — spróbuj ponownie.";
}
